import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  MapPinIcon,
  TrashIcon,
  ArrowTurnDownLeftIcon,
} from '@heroicons/react/24/outline';
import { MapIcon } from '@heroicons/react/24/solid';
import UnifiedMap from './UnifiedMap';
import LocationConsentService from '../services/LocationConsentService';

const MapPicker = ({ 
  value = null, // Can be single location object or array of waypoints
  onChange,
  mode = 'single', // 'single' or 'trail'
  onModeChange,
  className = '',
  height = '400px',
  hideControls = false, // Hide mode selector and action buttons
  searchResult = null // Search result from MapModal
}) => {
  const [waypoints, setWaypoints] = useState([]);
  const [singleLocation, setSingleLocation] = useState(null);
  const [userLocation, setUserLocation] = useState(null);

  const parsedValue = useMemo(() => {
    if (!value) return null;
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return value;
  }, [value]);

  // Get user location
  useEffect(() => {
    if (navigator.geolocation && LocationConsentService.canAccessLocation()) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.warn('Could not get user location:', error);
        }
      );
    }
  }, []);

  // Initialize from value prop
  useEffect(() => {
    const val = parsedValue;
    if (val) {
      const trailValue = Array.isArray(val)
        ? val
        : (Array.isArray(val.trail) ? val.trail : null);

      if (trailValue && trailValue.length > 0) {
        // Convert to waypoint format expected by UnifiedMap
        const formattedWaypoints = trailValue.map((point, index) => ({
          id: `waypoint-${index}`,
          position: [point.lat, point.lng],
          name: `Waypoint ${index + 1}`,
          timestamp: new Date().toISOString(),
        }));
        setWaypoints(formattedWaypoints);
        setSingleLocation(null);
      } else if (val.lat && val.lng) {
        setSingleLocation({ lat: val.lat, lng: val.lng });
        setWaypoints([]);
      }
    } else {
      setWaypoints([]);
      setSingleLocation(null);
    }
  }, [parsedValue]);

  // Handle map click for single location mode
  const handleMapClick = (latlng) => {
    if (mode === 'single') {
      const location = { lat: latlng.lat, lng: latlng.lng };
      setSingleLocation(location);
      onChange?.(location);
    }
  };

  // Handle waypoint addition for trail mode
  const handleWaypointAdd = (waypoint) => {
    const newWaypoints = [...waypoints, waypoint];
    setWaypoints(newWaypoints);
    
    // Convert back to simple lat/lng format for onChange
    const simpleWaypoints = newWaypoints.map(w => ({
      lat: w.position[0],
      lng: w.position[1]
    }));
    onChange?.(simpleWaypoints);
  };

  // Handle waypoints change (for undo operations from UnifiedMap)
  const handleWaypointsChange = (newWaypoints) => {
    setWaypoints(newWaypoints);
    
    if (newWaypoints.length === 0) {
      onChange?.(null);
    } else {
      // Convert back to simple lat/lng format for onChange
      const simpleWaypoints = newWaypoints.map(w => ({
        lat: w.position[0],
        lng: w.position[1]
      }));
      onChange?.(simpleWaypoints);
    }
  };

  // Remove last waypoint
  const handleRemoveLastWaypoint = () => {
    if (waypoints.length > 0) {
      const newWaypoints = waypoints.slice(0, -1);
      handleWaypointsChange(newWaypoints);
    }
  };

  // Clear all
  const handleClear = () => {
    setWaypoints([]);
    setSingleLocation(null);
    onChange?.(null);
  };

  // Get center and bounds for map initialization
  const getMapConfig = useCallback(() => {
    // For single location mode, center on the current location
    if (mode === 'single' && singleLocation) {
      return {
        center: [singleLocation.lat, singleLocation.lng],
        zoom: 13
      };
    }
    
    if (mode === 'trail' && waypoints.length > 0) {
      if (waypoints.length === 1) {
        return {
          center: waypoints[0].position,
          zoom: 13
        };
      }
      
      // Calculate bounds for multiple waypoints
      const lats = waypoints.map(w => w.position[0]);
      const lngs = waypoints.map(w => w.position[1]);
      
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);
      
      return {
        bounds: [[minLat, minLng], [maxLat, maxLng]]
      };
    }
    
    // Second priority: Use initial value prop if state hasn't been set yet
    if (parsedValue) {
      if (mode === 'single' && parsedValue.lat !== undefined && parsedValue.lng !== undefined) {
        return {
          center: [parsedValue.lat, parsedValue.lng],
          zoom: 13
        };
      }
      
      const rawTrail = Array.isArray(parsedValue)
        ? parsedValue
        : (Array.isArray(parsedValue.trail) ? parsedValue.trail : null);

      if (mode === 'trail' && rawTrail && rawTrail.length > 0) {
        if (rawTrail.length === 1) {
          return {
            center: [rawTrail[0].lat, rawTrail[0].lng],
            zoom: 13
          };
        }
        
        // Calculate bounds for multiple waypoints from raw value
        const lats = rawTrail.map(p => p.lat);
        const lngs = rawTrail.map(p => p.lng);
        
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        
        return {
          bounds: [[minLat, minLng], [maxLat, maxLng]]
        };
      }
    }
    
    // Default view
    return {
      center: userLocation ? [userLocation.lat, userLocation.lng] : [46.8182, 8.2275],
      zoom: userLocation ? 12 : 8
    };
  }, [mode, waypoints, parsedValue, userLocation, singleLocation]);

  const mapConfig = useMemo(() => getMapConfig(), [getMapConfig]);
  const hasData = singleLocation || waypoints.length > 0;
  const waypointCount = waypoints.length;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Mode selector and controls */}
      {!hideControls && (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onModeChange?.('single')}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              mode === 'single'
                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <MapPinIcon className="w-4 h-4" />
            Single Location
          </button>
          <button
            type="button"
            onClick={() => onModeChange?.('trail')}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              mode === 'trail'
                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <MapIcon className="w-4 h-4" />
            Trail/Route
          </button>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {mode === 'trail' && waypointCount > 0 && (
            <button
              type="button"
              onClick={handleRemoveLastWaypoint}
              className="flex items-center gap-2 px-3 py-2 bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 rounded-md text-sm font-medium hover:bg-orange-200 dark:hover:bg-orange-800 transition-colors"
            >
              <ArrowTurnDownLeftIcon className="w-4 h-4" />
              Undo Last
            </button>
          )}
          
          {hasData && (
            <button
              type="button"
              onClick={handleClear}
              className="flex items-center gap-2 px-3 py-2 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-md text-sm font-medium hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>
      </div>
      )}

      {/* Instructions */}
      {!hideControls && (
      <div className="text-sm text-gray-600 dark:text-gray-400">
        {mode === 'single' ? (
          <p></p>
        ) : (
          <div>
            {/* <p>Click on the map to add waypoints and create a trail route.</p> */}
            {waypointCount > 0 && (
              <p className="mt-1 text-xs">
                {waypointCount} waypoint{waypointCount !== 1 ? 's' : ''} added
                {waypointCount > 1 && ` • Trail distance: calculating...`}
              </p>
            )}
          </div>
        )}
      </div>
      )}

      {/* Map container */}
      <div 
        className="relative rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600 shadow-sm"
        style={{ height }}
      >
        {mode === 'single' ? (
          // Single location mode - show a marker on click, but no waypoint creation UI
          <UnifiedMap
            key="single-location-picker"
            mode="view"
            projects={singleLocation ? [{
              id: 'selected-location',
              name: 'Selected Location',
              geolocation: singleLocation,
            }] : []}
            userLocation={userLocation}
            layoutContext="contained"
            center={mapConfig.center}
            zoom={mapConfig.zoom}
            mapZoomTarget={searchResult ? {
              lat: searchResult.lat,
              lng: searchResult.lng,
              zoom: 13
            } : null}
            showUserLocation={true}
            showProjectNumbers={false}
            onMapClick={handleMapClick}
          />
        ) : (
          // Trail mode - use picker mode with waypoint functionality
          <UnifiedMap
            key="trail-picker"
            mode="picker"
            userLocation={userLocation}
            layoutContext="contained"
            center={mapConfig.center}
            zoom={mapConfig.zoom}
            bounds={mapConfig.bounds}
            showUserLocation={true}
            waypoints={waypoints}
            onWaypointAdd={handleWaypointAdd}
            onWaypointsChange={handleWaypointsChange}
          />
        )}
      </div>

      {/* Current selection info */}
      {!hideControls && hasData && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-sm">
          {mode === 'single' && singleLocation && (
            <div>
              <strong>Selected Location:</strong>
              <div className="mt-1 text-gray-600 dark:text-gray-400">
                Latitude: {singleLocation.lat.toFixed(6)}<br />
                Longitude: {singleLocation.lng.toFixed(6)}
              </div>
            </div>
          )}
          
          {mode === 'trail' && waypoints.length > 0 && (
            <div>
              <strong>Trail Waypoints:</strong>
              <div className="mt-1 space-y-1 max-h-32 overflow-y-auto">
                {waypoints.map((waypoint, index) => (
                  <div key={index} className="text-gray-600 dark:text-gray-400 text-xs">
                    {index + 1}. {waypoint.position[0].toFixed(4)}, {waypoint.position[1].toFixed(4)} - {waypoint.name}
                    {index === 0 && ' (Start)'}
                    {index === waypoints.length - 1 && waypoints.length > 1 && ' (End)'}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MapPicker;
